import { useCallback, useId } from 'react'
import { useIntl } from 'react-intl'
import { useT } from '_metronic/i18n/Metronici18n'
import { formatDateToPtBr } from '../../../../../../../_metronic/helpers'
import { SerieDiariaChart } from '../../../../../../components/Charts/SerieDiariaChart'
import type { PontoSerie } from '../../../../../../components/Charts/serieDiariaOpcoes'
import { ChaveIndicadorPerformance, IndicadorPerformance } from '../../core/_models'
import type { Agrupamento } from '../../core/agrupamento'
import {
  formatarContagem,
  formatarDuracao,
  formatarTaxaPercentual,
  formatarVariacao,
} from '../../core/formatacao'
import {
  AvaliacaoVariacao,
  avaliarVariacao,
  CHAVE_I18N_INDICADOR,
  contagemDerivada,
  ICONE_INDICADOR,
} from '../../core/performance'
import { AjudaIndicador, montarNotasAjuda } from '../ajuda/AjudaIndicador'

type Props = {
  chave: ChaveIndicadorPerformance
  indicador: IndicadorPerformance
  comparacaoMesCivil: boolean
  /** `grafico`: cartão com o gráfico do indicador; `taxa`: cartão ao lado das barras empilhadas. */
  variante: 'grafico' | 'taxa'
  /** Série já agregada pela seção (dia ou semana). */
  pontos?: PontoSerie[] | null
  agrupamento?: Agrupamento
  /** Nome do token CSS da série que o cartão representa nas barras (ex.: `--app-chart-series`). */
  corMarcador?: string
  /** Selo ao lado do nome ("Aproximado", "Espera até X min"). */
  selo?: string
  /** Decidido por `slaLimiteMinutos === null`, nunca pelo texto do rótulo. */
  slaNaoConfigurado?: boolean
  /** Linha complementar sempre visível (contagem de aferição única na triagem). */
  extra?: string
}

export const formatarValorIndicador = (
  valor: number | null,
  unidade: IndicadorPerformance['unidade']
): string | null =>
  unidade === 'MINUTOS' ? formatarDuracao(valor, 'minutos') : formatarTaxaPercentual(valor)

const COR_AVALIACAO: Record<AvaliacaoVariacao, string | undefined> = {
  melhora: 'var(--app-success-text)',
  piora: 'var(--app-danger-text)',
  neutra: undefined,
  estavel: undefined,
  sem_base: undefined,
}

const TEXTO_AVALIACAO: Record<AvaliacaoVariacao, string | undefined> = {
  melhora: 'indicadores.performance.variacao.melhorou',
  piora: 'indicadores.performance.variacao.piorou',
  neutra: 'indicadores.performance.variacao.sem_sentido',
  estavel: 'indicadores.performance.variacao.estavel',
  sem_base: undefined,
}

const iconeVariacao = (variacao: number): string =>
  variacao > 0 ? 'bi-arrow-up' : variacao < 0 ? 'bi-arrow-down' : 'bi-dash'

const formatarMinutos = (valor: number) => formatarDuracao(valor, 'minutos') ?? ''
const formatarTaxa = (valor: number) => formatarTaxaPercentual(valor) ?? ''
const formatoEixo = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 })
const formatarEixoMinutos = (valor: number) => formatoEixo.format(valor)
const formatarEixoPercentual = (valor: number) => `${formatoEixo.format(valor)}%`
export const formatarRotuloDia = (data: string) => formatDateToPtBr(data).slice(0, 5)

const legendaSerie = (unidade: IndicadorPerformance['unidade'], agrupamento: Agrupamento) => {
  const tipo = unidade === 'MINUTOS' ? 'media' : 'taxa'
  return `indicadores.performance.legenda.${tipo}_${
    agrupamento === 'semana' ? 'semanal' : 'diaria'
  }`
}

/**
 * Cartão de um indicador de performance: nome, valor, variação, contagem e, na variante
 * `grafico`, o gráfico do indicador com a média do período anterior. Estados decididos por
 * campo do contrato: SLA não configurado e sem dados (`valor` null). A melhora ou piora vem
 * em ícone e texto, além da cor.
 */
export const CardPerformance = ({
  chave,
  indicador,
  comparacaoMesCivil,
  variante,
  pontos = null,
  agrupamento = 'dia',
  corMarcador,
  selo,
  slaNaoConfigurado,
  extra,
}: Props) => {
  const { t } = useT()
  const intl = useIntl()
  const idBase = useId()
  const idTitulo = `${idBase}-titulo`
  const prefixo = CHAVE_I18N_INDICADOR[chave]
  const titulo = t(`${prefixo}.nome`)
  const emMinutos = indicador.unidade === 'MINUTOS'

  const notas = montarNotasAjuda(idBase, [indicador.rotulo])
  const valor = formatarValorIndicador(indicador.valor, indicador.unidade)
  const avaliacao = avaliarVariacao(chave, indicador.variacao)
  const variacao = formatarVariacao(
    indicador.variacao,
    indicador.tipoVariacao,
    t('indicadores.performance.variacao.pontos_percentuais')
  )
  const textoAvaliacao = TEXTO_AVALIACAO[avaliacao]

  const x = contagemDerivada(indicador)
  const contagem =
    x === null
      ? t(`${prefixo}.denominador`, { n: formatarContagem(indicador.n) })
      : t(`${prefixo}.contagem`, { x: formatarContagem(x), n: formatarContagem(indicador.n) })

  // Sem série não há gráfico nem legenda: o "sem dados" do valor já diz isso, uma vez só.
  const temSerie = Boolean(pontos?.some((ponto) => ponto.valor !== null))
  const temReferencia = indicador.valorPeriodoAnterior !== null
  const legendaTabela = t(
    agrupamento === 'semana'
      ? 'indicadores.performance.grafico.tabela_legenda_semana'
      : 'indicadores.performance.grafico.tabela_legenda',
    { indicador: titulo }
  )
  const formatarIntervalo = useCallback(
    (inicio: string, fim: string) =>
      // `intl` é estável entre renders; o `t` do useT não, e redesenharia o gráfico a cada render.
      intl.formatMessage(
        { id: 'indicadores.performance.grafico.intervalo' },
        { inicio: formatarRotuloDia(inicio), fim: formatarRotuloDia(fim) }
      ),
    [intl]
  )

  return (
    <div
      className='card card-flush h-100 border border-gray-300'
      role='group'
      aria-labelledby={idTitulo}
      aria-describedby={notas.length > 0 ? notas.map((nota) => nota.id).join(' ') : undefined}
    >
      <div className='card-body d-flex flex-column p-5'>
        <div className='d-flex justify-content-between align-items-start gap-2 mb-3'>
          <div className='d-flex align-items-center flex-wrap gap-2' style={{ minWidth: 0 }}>
            {variante === 'taxa' && corMarcador ? (
              <span
                className='d-inline-block rounded-1 flex-shrink-0'
                style={{ width: 12, height: 12, background: `var(${corMarcador})` }}
                aria-hidden='true'
              />
            ) : (
              <span className='symbol symbol-35px'>
                <span className='symbol-label bg-light-primary'>
                  <i
                    className={`bi ${ICONE_INDICADOR[chave]} fs-3 text-primary`}
                    aria-hidden='true'
                  />
                </span>
              </span>
            )}
            <h4 id={idTitulo} className='fs-6 fw-semibold text-gray-700 mb-0'>
              {titulo}
            </h4>
            {selo && !slaNaoConfigurado && (
              <span className='badge bg-light-primary text-gray-900 fw-semibold'>{selo}</span>
            )}
          </div>
          <AjudaIndicador rotulo={t('indicadores.ajuda.rotulo', { titulo })} notas={notas} />
        </div>

        {slaNaoConfigurado ? (
          <p className='d-flex align-items-start text-gray-700 fs-6 mb-0'>
            <i className='bi bi-slash-circle me-2 fs-5' aria-hidden='true' />
            <span>{indicador.rotulo}</span>
          </p>
        ) : (
          <>
            {valor === null ? (
              <p className='d-flex align-items-center text-gray-700 fs-6 mb-1'>
                <i className='bi bi-dash-circle me-2 fs-5' aria-hidden='true' />
                <span>{t('indicadores.card.sem_dados')}</span>
              </p>
            ) : (
              <p className='fs-2 fw-bold text-gray-900 mb-1'>{valor}</p>
            )}

            {variacao === null ? (
              <p className='fs-7 text-gray-700 mb-2'>
                {t('indicadores.performance.variacao.sem_base')}
              </p>
            ) : (
              <p className='fs-7 text-gray-700 mb-2'>
                {/* A cor fica no ícone (componente, 3:1): o texto do danger escuro não chega a 4,5:1. */}
                <i
                  className={`bi ${iconeVariacao(indicador.variacao ?? 0)} fs-6 me-1${
                    avaliacao === 'neutra' ? ' text-gray-700' : ''
                  }`}
                  style={COR_AVALIACAO[avaliacao] ? { color: COR_AVALIACAO[avaliacao] } : undefined}
                  aria-hidden='true'
                />
                <span className='fw-semibold text-gray-900'>{variacao}</span>{' '}
                <span>
                  {t(
                    comparacaoMesCivil
                      ? 'indicadores.performance.variacao.vs_mes_anterior'
                      : 'indicadores.performance.variacao.vs_periodo_anterior'
                  )}
                </span>
                {textoAvaliacao && (
                  <>
                    <span aria-hidden='true'> · </span>
                    <span className='fw-semibold text-gray-900'>{t(textoAvaliacao)}</span>
                  </>
                )}
              </p>
            )}

            <p className='fs-7 text-gray-700 mb-0'>{contagem}</p>
          </>
        )}

        {extra && <p className='fs-7 text-gray-700 mt-1 mb-0'>{extra}</p>}

        {variante === 'grafico' && !slaNaoConfigurado && (temSerie || valor !== null) && (
          <div className='mt-auto pt-4'>
            <div className='overflow-hidden'>
              <SerieDiariaChart
                pontos={pontos}
                nomeSerie={titulo}
                formatarValor={emMinutos ? formatarMinutos : formatarTaxa}
                formatarEixoY={emMinutos ? formatarEixoMinutos : formatarEixoPercentual}
                formatarRotuloDia={formatarRotuloDia}
                formatarIntervalo={formatarIntervalo}
                maximoY={emMinutos ? undefined : 100}
                valorReferencia={indicador.valorPeriodoAnterior}
                textoSemDados={t('indicadores.card.sem_dados')}
                tabela={{
                  legenda: legendaTabela,
                  colunaData: t(
                    agrupamento === 'semana'
                      ? 'indicadores.performance.grafico.coluna_semana'
                      : 'indicadores.performance.grafico.coluna_data'
                  ),
                  colunaValor: t('indicadores.performance.grafico.coluna_valor'),
                  colunaN: t('indicadores.performance.grafico.coluna_n'),
                }}
              />
            </div>
            {temSerie && (
              <ul className='list-unstyled d-flex flex-wrap gap-4 fs-7 text-gray-700 mt-2 mb-0'>
                <li className='d-flex align-items-center'>
                  <span
                    className='d-inline-block me-2'
                    style={{ width: 16, borderTop: '2px solid var(--app-chart-series)' }}
                    aria-hidden='true'
                  />
                  {t(legendaSerie(indicador.unidade, agrupamento))}
                </li>
                {temReferencia && (
                  <li className='d-flex align-items-center'>
                    <span
                      className='d-inline-block me-2'
                      style={{ width: 16, borderTop: '2px dashed var(--bs-gray-700)' }}
                      aria-hidden='true'
                    />
                    {t('indicadores.performance.legenda.media_periodo_anterior')}
                  </li>
                )}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
